import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Ion } from "@/components/Ion";
import { colors as C } from "@/lib/theme";

// Shared building blocks for the ported admin screens (console, broadcast, store, feedback).
export const ACCENT_BG = "rgba(76,204,6,0.08)";

export const AdminHeader = ({ eyebrow, title, right, icon = "chevron-back", back = "/admin" }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-4 p-6 pb-4">
      <button data-testid="back-to-hub-btn" onClick={() => navigate(back)} aria-label="Back" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border" style={{ background: C.card, borderColor: C.border }}>
        <Ion name={icon} size={22} color={C.text} />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold tracking-[3px]" style={{ color: C.accent }}>{eyebrow}</p>
        <h1 className="mt-0.5 truncate text-[22px] font-extrabold" data-testid="page-title">{title}</h1>
      </div>
      {right}
    </div>
  );
};

export const FormLabel = ({ children, className = "" }) => <p className={`mb-2 text-[11px] font-bold tracking-[3px] ${className}`} style={{ color: C.accent }}>{children}</p>;
export const Sublabel = ({ children, className = "" }) => <p className={`mb-1.5 mt-2 text-xs font-bold ${className}`} style={{ color: C.textSecondary }}>{children}</p>;
export const Help = ({ children, className = "" }) => <p className={`mb-4 text-xs ${className}`} style={{ color: C.textSecondary }}>{children}</p>;

export const inputCls = "mb-2 w-full rounded-lg border border-[#333] bg-[#1A1A1A] p-4 text-sm text-white placeholder:text-[#A0A0A0] focus:border-[var(--accent)] focus:outline-none";
export const Input = ({ multiline, rows = 4, className = "", ...p }) =>
  multiline ? <textarea rows={rows} {...p} className={`${inputCls} resize-y ${className}`} /> : <input {...p} className={`${inputCls} ${className}`} />;

export const BtnPrimary = ({ children, busy, className = "", ...p }) => (
  <button {...p} disabled={p.disabled || busy} className={`mt-4 flex w-full items-center justify-center gap-2 rounded-lg py-4 text-[13px] font-extrabold tracking-[2px] text-black transition-opacity disabled:opacity-60 ${className}`} style={{ background: C.accent, ...p.style }}>
    {busy && <Loader2 size={16} className="animate-spin" />}{children}
  </button>
);
export const BtnSecondary = ({ children, className = "", ...p }) => (
  <button {...p} className={`mt-4 flex items-center justify-center rounded-lg border px-4 py-4 text-xs font-bold tracking-[2px] ${className}`} style={{ background: C.card, borderColor: C.border, color: C.text }}>{children}</button>
);

export const Chip = ({ active, children, className = "", ...p }) => (
  <button {...p} className={`rounded-full border px-3 py-2 text-[11px] font-semibold uppercase tracking-[1px] transition-colors ${className}`}
    style={{ borderColor: active ? C.accent : C.border, background: active ? "rgba(76,204,6,0.1)" : C.card, color: active ? C.accent : C.textSecondary }}>{children}</button>
);
export const ChipRow = ({ children }) => <div className="mb-4 flex flex-wrap gap-1.5">{children}</div>;

export const IconAction = ({ icon, color = C.accent, bg = ACCENT_BG, border, title, className = "", ...p }) => (
  <button {...p} title={title} aria-label={title} className={`ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-transform hover:scale-105 ${className}`} style={{ borderColor: border || color, background: bg }}>
    <Ion name={icon} size={16} color={color} />
  </button>
);
export const DeleteBtn = (p) => <IconAction icon="trash-outline" color={C.error} bg="transparent" title="Delete" {...p} />;

export const RefreshBtn = ({ busy, ...p }) => (
  <button {...p} disabled={busy} className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[10px] font-bold tracking-[1.5px]" style={{ borderColor: C.border, background: C.surface, color: C.text }}>
    <Ion name="refresh" size={14} color={C.text} />{busy ? "…" : "REFRESH"}
  </button>
);

export const FilePicker = ({ accept, onPick, label, icon = "cloud-upload-outline", selected, onClear, testId }) => {
  const ref = useRef(null);
  return (
    <div className="mb-2 flex items-center gap-2 rounded-lg border border-dashed p-4" style={{ background: C.card, borderColor: C.accent }}>
      <input ref={ref} type="file" accept={accept} className="hidden" data-testid={`${testId}-input`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }} />
      <button type="button" onClick={() => ref.current?.click()} data-testid={testId} className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <Ion name={icon} size={20} color={C.accent} />
        <span className="truncate text-[13px] font-semibold">{label}</span>
      </button>
      {selected && onClear && <button type="button" onClick={onClear} aria-label="Clear file" data-testid={`${testId}-clear`}><Ion name="close-circle" size={20} color={C.textSecondary} /></button>}
    </div>
  );
};

export const Dialog = ({ open, onClose, children, testId, borderColor = C.error, maxWidth = 480 }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.85)" }} onClick={onClose}>
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-[14px] border p-8" style={{ maxWidth, background: C.card, borderColor }} onClick={(e) => e.stopPropagation()} data-testid={testId}>{children}</div>
    </div>
  );
};
export const DialogIcon = ({ name, color, bg, border = C.error }) => (
  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border" style={{ background: bg, borderColor: border }}><Ion name={name} size={26} color={color} /></div>
);

export const Badge = ({ icon, color = C.accent, bg = "rgba(76,204,6,0.1)", children, testId }) => (
  <span className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full border px-2 py-[3px] text-[9px] font-extrabold tracking-[1.5px]" style={{ borderColor: color, background: bg, color }} data-testid={testId}>
    {icon && <Ion name={icon} size={11} color={color} />}{children}
  </span>
);


export const Sheet = ({ open, onClose, title, children, footer, testId }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose}>
      <div className="w-full max-w-[680px] rounded-t-2xl border border-b-0 p-4 pb-8" style={{ background: C.card, borderColor: C.border }} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="mb-4 flex items-start justify-between gap-2.5">
          <p className="line-clamp-2 flex-1 text-[17px] font-extrabold">{title}</p>
          <button onClick={onClose} aria-label="Close" data-testid={`${testId}-close`}><Ion name="close" size={22} color={C.text} /></button>
        </div>
        <div className="max-h-[62vh] overflow-y-auto pr-1">{children}</div>
        {footer}
      </div>
    </div>
  );
};

export const Switch = ({ on, onChange, testId }) => (
  <button role="switch" aria-checked={!!on} onClick={() => onChange(!on)} data-testid={testId} className="relative h-[22px] w-[40px] shrink-0 rounded-full transition-colors" style={{ background: on ? C.accent : C.border }}>
    <span className="absolute top-[3px] h-4 w-4 rounded-full bg-white transition-[left]" style={{ left: on ? 21 : 3 }} />
  </button>
);

export const SearchBar = ({ value, onChange, placeholder, testId }) => (
  <div className="mx-4 mb-2 flex h-[42px] items-center gap-2 rounded-[10px] border px-3" style={{ background: C.card, borderColor: C.border }}>
    <Ion name="search-outline" size={16} color={C.textSecondary} />
    <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="flex-1 bg-transparent text-sm text-white placeholder:text-[#A0A0A0] focus:outline-none" />
    {value && <button onClick={() => onChange("")} aria-label="Clear search"><Ion name="close-circle" size={16} color={C.textSecondary} /></button>}
  </div>
);
